/**
 * Product cross-sell — pure gate + pricing (Pro feature).
 *
 * No I/O, no Firestore, no `firebase`. The Cloud Functions (`bookTicket`,
 * `appendItemsToTicket`) load the catalog + config, then call these to decide
 * whether products are allowed and to price the selection SERVER-SIDE. The
 * customer-pwa + dashboard call `planAllowsProductSales` for their gating too,
 * so the entitlement rule lives in exactly one place.
 *
 * The two-condition gate (both must be true before a product is sold):
 *   (a) plan entitlement — `planAllowsProductSales(plan)`
 *   (b) operator opt-in  — the tenant's `productSales/config.enabled`
 * The server is the boundary; the client is never trusted.
 */

import type { Product } from '../schemas/product.js';

/** The subset of a resolved plan this module reads. */
export interface PlanLikeForProducts {
  readonly features?: { readonly productSales?: boolean } | null;
}

/**
 * True when the tenant's plan grants the product cross-sell entitlement.
 * DEFAULT-OFF: a plan with no `features` map (or `productSales` absent/false)
 * resolves to false, so `starter` tenants — and any pre-feature plan doc —
 * are never entitled.
 */
export function planAllowsProductSales(plan: PlanLikeForProducts | null | undefined): boolean {
  return plan?.features?.productSales === true;
}

/**
 * True when BOTH halves of the gate are satisfied: the plan is entitled AND
 * the operator has opted in. This is the single predicate the client surfaces
 * (dashboard editor / customer picker) use to decide whether to render the
 * product UI at all. The server re-checks the same two conditions before
 * pricing — it never trusts this client-side resolution.
 */
export function productSalesLive(
  plan: PlanLikeForProducts | null | undefined,
  config: { readonly enabled?: boolean } | null | undefined,
): boolean {
  return planAllowsProductSales(plan) && config?.enabled === true;
}

/** One requested product line before pricing. */
export interface ProductSelectionInput {
  readonly productId: string;
  readonly qty: number;
}

/** One priced product line (mirrors `TicketProductLineSchema`). */
export interface PricedProductLine {
  readonly id: string;
  readonly name: string;
  readonly priceSen: number;
  readonly qty: number;
  readonly lineTotalSen: number;
}

export type ProductPricingErrorCode =
  | 'product_not_found'
  | 'product_inactive'
  | 'product_excluded'
  | 'product_qty_invalid'
  | 'product_qty_exceeded'
  | 'duplicate_product';

export type ProductPricingResult =
  | { readonly ok: true; readonly items: PricedProductLine[]; readonly totalSen: number }
  | {
      readonly ok: false;
      readonly code: ProductPricingErrorCode;
      readonly productId?: string;
    };

export interface PriceProductSelectionsArgs {
  /** The tenant's full product catalog (already loaded). */
  readonly products: readonly Product[];
  /** What the customer/staff chose. Empty = a no-op (money path unchanged). */
  readonly selections: readonly ProductSelectionInput[];
  /** The booking outlet — a product excluded here is refused. */
  readonly outletId: string;
  /** Preferred display locale for the snapshot `name` (falls back to en → id). */
  readonly locale?: string;
}

/**
 * Price a set of product selections against the catalog, SERVER-SIDE.
 *
 * Backward-compat contract: `selections: []` returns `{ ok: true, items: [],
 * totalSen: 0 }` and touches nothing — so a booking with no products prices
 * IDENTICALLY to before this feature existed. Only a non-empty selection can
 * change the total, and only after every line validates.
 *
 * Each selection is validated in a fixed order so error surfaces are stable:
 * duplicate → found → active → outlet-visible → qty ≥ 1 → qty ≤ max
 * (`maxQtyPerOrder ?? 1`). Returns a discriminated result rather than throwing
 * so callers map the code to their own error type (HttpsError in Functions).
 */
export function priceProductSelections(args: PriceProductSelectionsArgs): ProductPricingResult {
  const { products, selections, outletId, locale } = args;
  if (selections.length === 0) {
    return { ok: true, items: [], totalSen: 0 };
  }

  const byId = new Map(products.map((p) => [p.id, p]));
  const seen = new Set<string>();
  const items: PricedProductLine[] = [];
  let totalSen = 0;

  for (const sel of selections) {
    if (seen.has(sel.productId)) {
      return { ok: false, code: 'duplicate_product', productId: sel.productId };
    }
    seen.add(sel.productId);

    const product = byId.get(sel.productId);
    if (!product) {
      return { ok: false, code: 'product_not_found', productId: sel.productId };
    }
    if (!product.active) {
      return { ok: false, code: 'product_inactive', productId: sel.productId };
    }
    if ((product.excludedOutletIds ?? []).includes(outletId)) {
      return { ok: false, code: 'product_excluded', productId: sel.productId };
    }
    if (!Number.isInteger(sel.qty) || sel.qty < 1) {
      return { ok: false, code: 'product_qty_invalid', productId: sel.productId };
    }
    const maxQty = product.maxQtyPerOrder ?? 1;
    if (sel.qty > maxQty) {
      return { ok: false, code: 'product_qty_exceeded', productId: sel.productId };
    }

    const name = product.name[locale ?? 'en'] ?? product.name['en'] ?? product.id;
    const lineTotalSen = product.priceSen * sel.qty;
    items.push({ id: product.id, name, priceSen: product.priceSen, qty: sel.qty, lineTotalSen });
    totalSen += lineTotalSen;
  }

  return { ok: true, items, totalSen };
}
